import { useMemo, useState } from 'react';
import {
  CANCELLATION_REASON_CODES,
  COMMONLY_SEEN_CODES,
  ISO_REASON_CODE_GROUPS,
  MESSAGE_REJECT_CODES,
  PROPRIETARY_REASON_CODES,
  REFERENCE_NOTES,
  TRANSACTION_STATUS_CODES,
  TRANSACTION_STATUS_INTERNAL_MAPPING,
} from '../data/instapayErrorCodes';

// Static reference page - every code on this page comes from
// dashboard/frontend/src/data/instapayErrorCodes.js (transcribed from the
// InstaPay ISO 20022 error/reason code PDF), not from any API/Redis/MySQL
// source. Unlike every other page in this dashboard, there is no loading
// state, no `api/client.js` call, and nothing to Refresh - the content is
// baked into the frontend bundle and only changes when that data file does.

// One flat, search-friendly list built once from every section on this
// page, so the search box can match across categories instead of only
// within whichever section a code happens to live in.
function buildSearchIndex() {
  const rows = [];

  TRANSACTION_STATUS_CODES.forEach((c) =>
    rows.push({ category: 'Transaction status (pacs.002 TxSts)', code: c.code, name: c.name, meaning: c.meaning })
  );

  ISO_REASON_CODE_GROUPS.forEach((group) => {
    group.codes.forEach((c) =>
      rows.push({
        category: `ISO reason code - ${group.prefix} (${group.title})`,
        code: c.code,
        name: null,
        meaning: c.meaning,
      })
    );
  });

  PROPRIETARY_REASON_CODES.forEach((c) =>
    rows.push({ category: 'InstaPay proprietary reason code', code: c.code, name: null, meaning: c.meaning })
  );

  CANCELLATION_REASON_CODES.forEach((c) =>
    rows.push({ category: 'Cancellation reason code (camt.056)', code: c.code, name: null, meaning: c.meaning })
  );

  MESSAGE_REJECT_CODES.forEach((c) =>
    rows.push({ category: 'Message reject code (admi.002)', code: c.code, name: c.name, meaning: c.meaning })
  );

  return rows;
}

const SEARCH_INDEX = buildSearchIndex();

function CodeBadge({ code, highlighted }) {
  return (
    <span className={`badge font-monospace ${highlighted ? 'text-bg-primary' : 'text-bg-light border'}`}>
      {code}
    </span>
  );
}

export default function ErrorCodes() {
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!q) {
      return null;
    }
    return SEARCH_INDEX.filter(
      (row) =>
        row.code.toLowerCase().includes(q) ||
        (row.name && row.name.toLowerCase().includes(q)) ||
        row.meaning.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div>
      <h1 className="h4 mb-2">InstaPay Error Codes</h1>
      <p className="text-muted small" style={{ maxWidth: '90%' }}>
        Reference for InstaPay ISO 20022 transaction status, reason, cancellation, and message-reject
        codes. Static content transcribed from the InstaPay error/reason code documentation - not
        sourced from this service&rsquo;s own data.
      </p>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: '420px' }}
          placeholder="Search by code, e.g. AC13, DS24, 9955, or a keyword like &quot;account&quot;"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {searchResults ? (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '9rem' }}>Code</th>
                  <th>Meaning</th>
                  <th style={{ width: '20rem' }}>Category</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((row, idx) => (
                  <tr key={`${row.category}-${row.code}-${idx}`}>
                    <td>
                      <CodeBadge code={row.code} highlighted={COMMONLY_SEEN_CODES.includes(row.code)} />
                    </td>
                    <td>
                      {row.name && <div className="fw-semibold">{row.name}</div>}
                      <div className={row.name ? 'text-muted small' : ''}>{row.meaning}</div>
                    </td>
                    <td className="text-muted small">{row.category}</td>
                  </tr>
                ))}
                {searchResults.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-4">
                      No codes match &ldquo;{query}&rdquo;.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6 mb-3">Commonly seen in InstaPay flows</h2>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {COMMONLY_SEEN_CODES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className="btn btn-sm btn-outline-primary font-monospace"
                    onClick={() => setQuery(code)}
                  >
                    {code}
                  </button>
                ))}
              </div>
              <p className="text-muted small mb-0">Click a code to jump straight to its meaning.</p>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6 mb-3">Transaction status codes (pacs.002 TxSts)</h2>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '8rem' }}>Code</th>
                      <th style={{ width: '16rem' }}>Name</th>
                      <th>Meaning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TRANSACTION_STATUS_CODES.map((c) => (
                      <tr key={c.code}>
                        <td>
                          <CodeBadge code={c.code} />
                        </td>
                        <td>{c.name}</td>
                        <td className="text-muted">{c.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted small mt-3 mb-0">
                Internal mapping used by this pipeline:{' '}
                {TRANSACTION_STATUS_INTERNAL_MAPPING.map((m, idx) => (
                  <span key={m.code}>
                    {idx > 0 && ', '}
                    <span className="font-monospace">{m.code}</span> &rarr; {m.internal}
                  </span>
                ))}
                .
              </p>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="h6 mb-0">ISO reason codes (pacs.002 StsRsnInf/Rsn/Cd)</h2>
                <span className="text-muted small">
                  {ISO_REASON_CODE_GROUPS.reduce((sum, g) => sum + g.codes.length, 0)} codes across{' '}
                  {ISO_REASON_CODE_GROUPS.length} families
                </span>
              </div>
              <div className="accordion" id="isoReasonCodeAccordion">
                {ISO_REASON_CODE_GROUPS.map((group, idx) => {
                  const panelId = `iso-group-${group.prefix}`;
                  return (
                    <div className="accordion-item" key={group.prefix}>
                      <h3 className="accordion-header">
                        <button
                          className={`accordion-button ${idx === 0 ? '' : 'collapsed'}`}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#${panelId}`}
                          aria-expanded={idx === 0 ? 'true' : 'false'}
                          aria-controls={panelId}
                        >
                          <span className="font-monospace me-2">{group.prefix}xx</span>
                          {group.title}
                          <span className="text-muted small ms-2">({group.codes.length})</span>
                        </button>
                      </h3>
                      <div
                        id={panelId}
                        className={`accordion-collapse collapse ${idx === 0 ? 'show' : ''}`}
                        data-bs-parent="#isoReasonCodeAccordion"
                      >
                        <div className="accordion-body p-0">
                          <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: '8rem' }}>Code</th>
                                  <th>Meaning</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.codes.map((c) => (
                                  <tr key={c.code}>
                                    <td>
                                      <CodeBadge
                                        code={c.code}
                                        highlighted={COMMONLY_SEEN_CODES.includes(c.code)}
                                      />
                                    </td>
                                    <td className="text-muted">{c.meaning}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h2 className="h6 mb-3">InstaPay proprietary reason codes (pacs.002 StsRsnInf/Rsn/Prtry)</h2>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '7rem' }}>Code</th>
                          <th>Meaning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PROPRIETARY_REASON_CODES.map((c) => (
                          <tr key={c.code}>
                            <td>
                              <CodeBadge code={c.code} highlighted={COMMONLY_SEEN_CODES.includes(c.code)} />
                            </td>
                            <td className="text-muted">{c.meaning}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h2 className="h6 mb-3">Cancellation reason codes (camt.056 CxlRsnInf/Rsn/Cd)</h2>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '7rem' }}>Code</th>
                          <th>Meaning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {CANCELLATION_REASON_CODES.map((c) => (
                          <tr key={c.code}>
                            <td>
                              <CodeBadge code={c.code} />
                            </td>
                            <td className="text-muted">{c.meaning}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h6 mb-3">Message reject codes (admi.002 Administration Advice)</h2>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '7rem' }}>Code</th>
                      <th style={{ width: '18rem' }}>Name</th>
                      <th>When it is returned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MESSAGE_REJECT_CODES.map((c) => (
                      <tr key={c.code}>
                        <td>
                          <CodeBadge code={c.code} />
                        </td>
                        <td className="fw-semibold">{c.name}</td>
                        <td className="text-muted">{c.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h2 className="h6 mb-3">Notes</h2>
              <ul className="text-muted small mb-0 ps-3">
                {REFERENCE_NOTES.map((note) => (
                  <li key={note} className="mb-1">
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
